/**
 * The template list's own reference helpers — `m02-mockdb.js` §template accessors and
 * §`duplicateTemplateRecord`.
 *
 * They are here rather than in `reference.ts` because that file is shared by every screen and is
 * being edited by nobody: what the Şablonlar screen needs beyond it is the credential/subject type
 * vocabularies its filter bar offers, the four values a card's meta row reads off a layout, and
 * the "Kopyala" record the reference builds in memory.
 */
import constants from '../data/demo-constants.json';
import type {
  DemoCanvas,
  DemoDatabase,
  DemoLocalizedMetadata,
  DemoTemplate,
  DemoValidityPolicy,
} from '../data/types';
import { currentRelease, nowUtcSeconds } from './reference';
import { typeRec } from './render';

export interface TypeOption {
  key: string;
  tr: string;
  en: string;
}

/** The filter bar's two vocabularies, in the reference's own order — it is the option order too. */
export const CREDENTIAL_TYPES = constants.CREDENTIAL_TYPES as TypeOption[];
export const SUBJECT_TYPES = constants.SUBJECT_TYPES as TypeOption[];

/**
 * The subset of a release/draft the list reads. Both shapes carry these keys, and the seed types
 * them loosely enough that a `DemoRelease | DemoDraft` union collapses to its index signature —
 * so the layout is narrowed here, once, rather than at every call site.
 */
export interface TemplateLayout {
  credentialType?: string;
  customTypeLabel?: string | null;
  subjectType?: string;
  canvas: DemoCanvas;
  layers?: unknown[];
  fieldDefinitions?: unknown[];
  validityPolicy?: DemoValidityPolicy | null;
  localizedMetadata?: Record<string, DemoLocalizedMetadata>;
}

/** `layoutOf` — the published release when there is one, the draft otherwise. Preview only. */
export const layoutOf = (t: DemoTemplate): TemplateLayout =>
  (currentRelease(t) ?? t.draft) as unknown as TemplateLayout;

/** `templateTypeLabel` — a custom type shows its own label, every other type its vocabulary name. */
export function templateTypeLabel(t: DemoTemplate): string {
  const lay = layoutOf(t);
  if (lay.credentialType === 'custom' && lay.customTypeLabel) return lay.customTypeLabel;
  return typeRec(lay.credentialType).tr;
}

/** `draftTitle` — the card's subtitle is the DRAFT's title even when a release is published. */
export const draftTitle = (t: DemoTemplate, lang: 'tr' | 'en'): string =>
  t.draft.localizedMetadata[lang === 'en' ? 'en' : 'tr']?.title || t.name;

/**
 * `tplCount` — how many credentials were produced from this template, across all releases.
 *
 * The server's count when one has been read, and only otherwise the records in the store. Counting
 * the store is right for the demo, where the seed is the whole register, and wrong for the product,
 * where it is a page of it: a template with four hundred credentials would have reported however
 * many of them happened to be on screen, and «henüz kayıt üretilmedi» about the rest.
 */
export const tplCount = (db: DemoDatabase, id: string): number =>
  db.credentialCountByTemplate?.[id] ?? db.credentials.filter((c) => c.templateId === id).length;

/**
 * `prototypeNowUTC` — the prototype's clock, served by the view clock.
 *
 * In the demo today is still the frozen prototype date with the real time of day, so a record
 * touched there sorts and formats like a record the reference touched. In the product it is the
 * real instant, so a draft saved today does not claim to have been saved in the past.
 */
export const prototypeNowUTC = nowUtcSeconds;

/** The keys a draft may carry; a release's identity fields are deliberately not among them. */
const DRAFT_KEYS = [
  'credentialType',
  'customTypeLabel',
  'subjectType',
  'holderField',
  'defaultLanguage',
  'localizedMetadata',
  'validityPolicy',
  'publicationPolicy',
  'permissions',
  'duplicatePolicy',
  'emailDefaults',
  'interoperability',
  'canvas',
  'background',
  'fieldDefinitions',
  'layers',
  'safeAreas',
  'renderProfile',
] as const;

let uidCounter = 1000;
const uid = (prefix: string): string => `${prefix}_${(++uidCounter).toString(36)}`;

/**
 * `duplicateTemplateRecord` — the copy is a DRAFT of the source's CURRENT RELEASE, never a second
 * publication of it: no releases, no `currentReleaseId`, and fresh field and layer ids so editing
 * the copy can never reach into the original's snapshots (rules 3 and 4).
 */
/**
 * A short code the copy can actually be created with.
 *
 * The reference's copy keeps the source's code, because its templates live in a tab and nothing
 * enforces uniqueness. `templates.code` IS unique in the product, so a copy that kept the code
 * collided every single time and «Kopyala» was a button that could only fail. The suffix follows
 * the reference's own naming of the copy («… (kopya)») and the backend remains the arbiter: if the
 * derived code is still taken, its 409 is shown as the product's own words.
 */
function copyCode(code: string, taken: ReadonlySet<string>): string {
  const base = `${code}-kopya`;
  if (!taken.has(base)) return base;
  for (let n = 2; n < 100; n += 1) {
    const candidate = `${base}-${n}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base}-${Math.random().toString(36).slice(2, 8)}`;
}

export function duplicateTemplateRecord(
  t: DemoTemplate,
  taken: ReadonlySet<string> = new Set(),
): DemoTemplate {
  const source = structuredClone(currentRelease(t) ?? t.draft) as unknown as Record<string, unknown>;
  const draft: Record<string, unknown> = {};
  for (const key of DRAFT_KEYS) if (source[key] !== undefined) draft[key] = source[key];
  const withNewId = <T>(list: unknown, prefix: string): T[] =>
    (Array.isArray(list) ? list : []).map((x) => ({ ...(x as object), id: uid(prefix) }) as T);
  draft.fieldDefinitions = withNewId(draft.fieldDefinitions, 'fld');
  draft.layers = withNewId(draft.layers, 'lyr');

  const now = prototypeNowUTC();
  return {
    id: uid('tpl'),
    code: copyCode(t.code, taken),
    name: `${t.name} (kopya)`,
    status: 'draft',
    currentReleaseId: null,
    releases: [],
    draft: draft as unknown as DemoTemplate['draft'],
    seeded: false,
    userModified: true,
    createdAt: now,
    updatedAt: now,
  };
}
